/**
 * Provides best-effort Midway token retrieval while callers decide how failures affect the session.
 */
export const MIDWAY_DOMAIN = 'midway-auth.amazon.com';

type QueryParameters = Record<string, string>;

// In-memory cache for token
let cachedToken: string | null = null;
let tokenExpiration: number | null = null;

export function generateNonce (): string {
  const characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(64);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => characterSet[byte % characterSet.length]).join('');
}

function buildQuery (parameters: QueryParameters): string {
  const query = Object.keys(parameters)
    .map((key) => `${key}=${encodeURIComponent(parameters[key])}`)
    .join('&');
  return `?${query}`;
}

function buildSSOUrl (): string {
  const queryParams: QueryParameters = {
    response_type: 'id_token',
    client_id: window.location.host,
    redirect_uri: window.location.href,
    scope: 'openid',
    nonce: generateNonce(),
  };

  return `https://${MIDWAY_DOMAIN}/SSO${buildQuery(queryParams)}`;
}

function buildRedirectUrl (): string {
  const queryParams: QueryParameters = {
    client_id: window.location.host,
    redirect_uri: window.location.href,
    response_type: 'id_token',
    scope: 'openid',
    nonce: generateNonce(),
  };

  return `https://${MIDWAY_DOMAIN}/login?next=/SSO/redirect${encodeURIComponent(
    buildQuery(queryParams),
  )}`;
}

export function redirectToMidwayLogin (): void {
  window.location.href = buildRedirectUrl();
}

/**
 * Decodes JWT payload to extract expiration time
 */
function decodeTokenExpiration (token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Checks if cached token is still valid
 */
function isTokenValid (): boolean {
  if (!cachedToken || !tokenExpiration) {
    return false;
  }
  // Add 30 second buffer before expiration
  const now = Date.now();
  return now < tokenExpiration - 30000;
}

/**
 * Fetches a fresh token from Midway
 */
async function fetchFreshToken (): Promise<string | undefined> {
  const response = await fetch(buildSSOUrl(), { credentials: 'include' });

  if (!response.ok) {
    return undefined;
  }

  const token = await response.text();
  const expiration = decodeTokenExpiration(token);

  cachedToken = token;
  tokenExpiration = expiration ?? Date.now() + 3600000;

  return token;
}

/**
 * Retrieves a Midway JWT token with smart caching.
 * @returns The JWT token, or undefined when token retrieval fails.
 */
const getMidwayToken = async (): Promise<string | undefined> => {
  // Skip Midway auth in development and test environments
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return 'mock-midway-token';
  }

  try {
    // Return cached token if still valid
    if (isTokenValid() && cachedToken) {
      return cachedToken;
    }

    // Fetch fresh token if expired or missing
    return await fetchFreshToken();
  } catch (error) {
    console.error('Failed to retrieve Midway token:', error);
    return undefined;
  }
};

/**
 * Clears the cached token (useful for logout)
 */
export function clearMidwayToken (): void {
  cachedToken = null;
  tokenExpiration = null;
}

export default getMidwayToken;
